/**
 * Mock recall search and the recall itself (F7.9): the code box takes a pouch / carton link, a pack
 * lot, batch or lot code, or a sack tag; the recall needs a reason (10–200, like RecallDto) and
 * the person's own password (re-authentication for the signature).
 */
const LINK_LOT = /\/10\/(PL-\d{4}-\d{4}-\d{4}-[A-Z0-9])\s*$/i

/** Trimmed code for the search; a pouch link is kept whole (the server reads links). */
export function cleanRecallCode(raw: string): string {
  return raw.trim()
}

export function validateRecallCode(raw: string): string | null {
  const code = cleanRecallCode(raw)
  if (!code) return 'Scan or type a code'
  if (code.length > 200) return 'Too long for a code'
  return null
}

/** The pack lot code inside a pouch link, or the code itself (upper case) — for lookups by code. */
export function codeForLookup(raw: string): string {
  const t = raw.trim()
  const m = LINK_LOT.exec(t)
  return m ? m[1]!.toUpperCase() : t
}

export interface RecallFormValues {
  reason: string
  password: string
}
export type RecallFormErrors = Partial<Record<keyof RecallFormValues, string>>

export function validateRecallForm(f: RecallFormValues): RecallFormErrors {
  const e: RecallFormErrors = {}
  const reason = f.reason.trim()
  if (reason.length < 10) e.reason = 'Say why (at least 10 characters)'
  else if (reason.length > 200) e.reason = 'At most 200 characters'
  if (!f.password) e.password = 'Type your password to sign'
  return e
}

/**
 * What a recall of this code will hold, in words. A pack lot holds itself; a farmer lot holds every
 * pack lot made from it (the trace's pack lots); a batch holds only what was made from that batch,
 * which the trace (it walks back to the farmer lots first) cannot count exactly.
 */
export function recallScopeSentence(startType: string, packLots: number, pouchesOut: number): string {
  const lots = `${packLots} pack ${packLots === 1 ? 'lot' : 'lots'}`
  const base =
    startType === 'PACK'
      ? 'This pack lot will be put on recall hold.'
      : startType === 'LOT'
        ? `Every pack lot made from this farmer lot (${lots}) will be put on recall hold, and the batches in between are marked recalled.`
        : `Every pack lot made from this ${startType === 'BATCH' ? 'batch' : 'code'} will be put on recall hold, and what lies in between is marked recalled.`
  const out = pouchesOut > 0 ? ` ${pouchesOut} pouches have already gone to customers — the customer list is who to call.` : ''
  return `${base} Pouch scans will show the recall page.${out}`
}
